/**
 * Subscribe & Save starter-kit bundles.
 * - Add: Plan Cards sends main + bonuses in one cart/add.js (see buy-buttons.liquid).
 * - Qty: cart drawer updates main + all bonus lines in one cart/update.js (no debounced reconcile).
 * - Repair: on other cart:update events, adds missing bonuses / removes orphans only.
 */

const BUNDLE_ID_PROP = '_pc_bundle_id';
const BUNDLE_ROLE_PROP = '_pc_bundle_role';
const SOURCE = 'subscription-bundle-manager';

class SubscriptionBundleManager {
  #processing = false;
  #pendingReconcile = false;
  #debounceTimer = null;
  /** @type {object|null} */
  #config = null;

  constructor() {
    this.#loadConfig();

    document.addEventListener('cart:update', (event) => {
      if (event.detail?.data?.source === SOURCE) return;
      // Main-line qty changes use syncMainLineQuantity (single cart/update.js).
      if (event.detail?.data?.source === 'cart-items-component') return;
      this.#scheduleReconcile(120);
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#scheduleReconcile(0));
    } else {
      this.#scheduleReconcile(0);
    }
  }

  get isBusy() {
    return this.#processing;
  }

  willSyncBundleQuantity() {
    return Boolean(this.#config?.enabled && this.#config?.matchMainQuantity !== false);
  }

  #loadConfig() {
    const el = document.getElementById('SubscriptionBonusConfig');
    if (!el) return;
    try {
      this.#config = JSON.parse(el.textContent);
    } catch (error) {
      console.warn('[SubscriptionBundleManager] Could not parse config', error);
    }
  }

  /** @param {number} [delay] */
  #scheduleReconcile(delay = 120) {
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.#reconcile(), delay);
  }

  #getSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
  }

  #createBundleId() {
    return 'pc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
  }

  /** @param {object} item @param {Map<string, object>} itemsByKey */
  #isBonusLine(item, itemsByKey) {
    const props = item.properties || {};
    if (props[BUNDLE_ROLE_PROP] === 'main') return false;
    if (props[BUNDLE_ROLE_PROP] === 'bonus') return true;

    const parentKey = item.parent_relationship?.parent_key;
    if (parentKey) {
      const parent = itemsByKey.get(parentKey);
      if (parent?.selling_plan_allocation) return true;
    }

    const bonusIds = (this.#config?.bonusVariantIds || []).map(String);
    return bonusIds.includes(String(item.variant_id));
  }

  /** @param {object} item @param {Map<string, object>} itemsByKey @param {object[]} allItems */
  #isSubscriptionMain(item, itemsByKey, allItems) {
    if (!item.selling_plan_allocation) return false;
    if (item.properties?.[BUNDLE_ROLE_PROP] === 'bonus') return false;
    if (this.#isBonusLine(item, itemsByKey)) return false;

    const tierMap = this.#config?.tierVariants || {};
    if (tierMap[String(item.variant_id)]) return true;
    if (item.properties?.[BUNDLE_ROLE_PROP] === 'main') return true;

    return allItems.some(
      (child) =>
        child.parent_relationship?.parent_key === item.key &&
        this.#isBonusLine(child, itemsByKey)
    );
  }

  /** @param {object} main @param {object[]} bonuses @returns {string|null} */
  #resolveTier(main, bonuses) {
    const tierFromVariant = this.#config?.tierVariants?.[String(main.variant_id)];
    if (tierFromVariant) return tierFromVariant;

    for (const tier of ['1', '2']) {
      const expectedIds = this.#expectedBonuses(tier).map((b) => String(b.variantId));
      if (bonuses.some((b) => expectedIds.includes(String(b.variant_id)))) {
        return tier;
      }
    }

    return null;
  }

  /** @param {string} tier @returns {Array<{variantId: number, quantity: number}>} */
  #expectedBonuses(tier) {
    return (this.#config?.[tier] || []).filter((b) => b && b.variantId);
  }

  /**
   * @param {Record<string, number>} updates
   * @param {string[]} [sectionIds]
   */
  async #postCartUpdate(updates, sectionIds) {
    /** @type {Record<string, unknown>} */
    const body = { updates };
    if (sectionIds?.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.warn('[SubscriptionBundleManager] update.js failed');
      return null;
    }

    return response.json();
  }

  /**
   * @param {string} lineKey
   * @param {number} quantity
   * @param {string[]} sectionIds
   * @param {boolean} includeSectionsOnLast
   */
  async #changeLineQuantity(lineKey, quantity, sectionIds, includeSectionsOnLast) {
    /** @type {Record<string, unknown>} */
    const body = { id: lineKey, quantity };
    if (includeSectionsOnLast && sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      console.warn('[SubscriptionBundleManager] change.js failed', lineKey, quantity);
      return null;
    }

    return response.json();
  }

  /** @param {Map<string, number>} updates @param {Array<object>} itemsToAdd */
  async #applyCartChanges(updates, itemsToAdd) {
    const sectionIds = this.#getSectionIds();
    const updateEntries = Array.from(updates.entries()).filter(([, qty]) => qty !== undefined);
    const hasUpdates = updateEntries.length > 0;
    const hasAdds = itemsToAdd.length > 0;

    if (!hasUpdates && !hasAdds) return null;

    let cartData = null;

    for (let i = 0; i < updateEntries.length; i++) {
      const [lineKey, quantity] = updateEntries[i];
      const isLast = i === updateEntries.length - 1 && !hasAdds;
      cartData = await this.#changeLineQuantity(lineKey, quantity, sectionIds, isLast);
    }

    if (hasAdds) {
      /** @type {Record<string, unknown>} */
      const body = { items: itemsToAdd };
      if (sectionIds.length) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        console.warn('[SubscriptionBundleManager] add.js failed');
        return cartData;
      }
      cartData = await response.json();
    }

    if (cartData) {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartData,
            sourceId: SOURCE,
            data: {
              source: SOURCE,
              itemCount: cartData.item_count,
              sections: cartData.sections || {},
            },
          },
        })
      );
    }

    return cartData;
  }

  /**
   * Update subscription main + linked bonus quantities in one request.
   * @param {number} lineIndex - 1-based cart line index
   * @param {number} newQty
   * @param {string[]} sectionIds
   * @returns {Promise<object|null>}
   */
  async syncMainLineQuantity(lineIndex, newQty, sectionIds) {
    if (!this.willSyncBundleQuantity()) return null;

    this.#processing = true;
    try {
      const items = await this.#fetchCartItems();
      if (!items?.length) return null;

      const mainItem = items[lineIndex - 1];
      if (!mainItem) return null;

      const itemsByKey = new Map(items.map((item) => [item.key, item]));
      if (!this.#isSubscriptionMain(mainItem, itemsByKey, items)) return null;

      const { groups } = this.#buildMainGroups(items);
      const group = groups.find((g) => g.main.key === mainItem.key);
      if (!group?.tier) return null;

      const expected = this.#expectedBonuses(group.tier);
      if (!expected.length) return null;

      const mainQty = Math.max(parseInt(String(newQty), 10) || 0, 0);
      /** @type {Record<string, number>} */
      const updates = { [mainItem.key]: mainQty };

      const expectedIds = expected.map((b) => String(b.variantId));

      group.bonuses.forEach((bonus) => {
        if (!expectedIds.includes(String(bonus.variant_id))) return;
        const bonusDef = expected.find((b) => String(b.variantId) === String(bonus.variant_id));
        if (!bonusDef) return;
        const baseQty = parseInt(bonusDef.quantity, 10) || 1;
        updates[bonus.key] = baseQty * mainQty;
      });

      const cartData = await this.#postCartUpdate(updates, sectionIds);
      return cartData;
    } catch (error) {
      console.error('[SubscriptionBundleManager] syncMainLineQuantity', error);
      return null;
    } finally {
      this.#processing = false;
    }
  }

  /**
   * @param {object} item
   * @param {string} bundleId
   */
  async #stampMainLine(item, bundleId) {
    const props = { ...(item.properties || {}), [BUNDLE_ID_PROP]: bundleId, [BUNDLE_ROLE_PROP]: 'main' };
    const sectionIds = this.#getSectionIds();

    /** @type {Record<string, unknown>} */
    const body = {
      id: item.key,
      quantity: item.quantity,
      properties: props,
    };

    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /**
   * @param {object[]} items
   */
  #buildMainGroups(items) {
    /** @type {Map<string, object>} */
    const itemsByKey = new Map(items.map((item) => [item.key, item]));
    /** @type {Set<string>} */
    const assignedBonusKeys = new Set();
    /** @type {Array<{main: object, bonuses: object[], tier: string|null}>} */
    const groups = [];

    items.forEach((item) => {
      if (!this.#isSubscriptionMain(item, itemsByKey, items)) return;

      const bonuses = items.filter((candidate) => {
        if (candidate.key === item.key) return false;
        if (!this.#isBonusLine(candidate, itemsByKey)) return false;

        const parentKey = candidate.parent_relationship?.parent_key;
        if (parentKey === item.key) return true;

        const mainBundleId = item.properties?.[BUNDLE_ID_PROP];
        const bonusBundleId = candidate.properties?.[BUNDLE_ID_PROP];
        if (mainBundleId && bonusBundleId && mainBundleId === bonusBundleId) return true;

        return false;
      });

      const tier = this.#resolveTier(item, bonuses);
      groups.push({ main: item, bonuses, tier });
      bonuses.forEach((bonus) => assignedBonusKeys.add(bonus.key));
    });

    const orphans = items.filter(
      (item) => this.#isBonusLine(item, itemsByKey) && !assignedBonusKeys.has(item.key)
    );

    return { groups, orphans, itemsByKey };
  }

  async #fetchCartItems() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const cart = await response.json();
    return cart.items || [];
  }

  /**
   * @param {object[]} items
   * @returns {Array<{item: object, bundleId: string}>}
   */
  #findMainsToStamp(items) {
    const { groups } = this.#buildMainGroups(items);
    /** @type {Array<{item: object, bundleId: string}>} */
    const toStamp = [];

    groups.forEach(({ main, tier }) => {
      if (!tier) return;
      if (!main.properties?.[BUNDLE_ID_PROP]) {
        toStamp.push({ item: main, bundleId: this.#createBundleId() });
      }
    });

    return toStamp;
  }

  /**
   * Add missing bonus lines and remove orphans / wrong-tier bonuses. Does not change qty on existing lines.
   * @param {object[]} items
   */
  async #repairBonusLines(items) {
    /** @type {Map<string, number>} */
    const updates = new Map();
    /** @type {Array<object>} */
    const itemsToAdd = [];
    const { groups, orphans, itemsByKey } = this.#buildMainGroups(items);

    orphans.forEach((bonus) => {
      const parentKey = bonus.parent_relationship?.parent_key;
      if (parentKey && itemsByKey.has(parentKey)) return;
      updates.set(bonus.key, 0);
    });

    groups.forEach(({ main, bonuses, tier }) => {
      if (!tier) return;

      const bundleId = main.properties?.[BUNDLE_ID_PROP] || this.#createBundleId();
      const expected = this.#expectedBonuses(tier);
      if (!expected.length) return;

      const expectedIds = expected.map((b) => String(b.variantId));
      const mainQty = parseInt(main.quantity, 10) || 1;
      const matchMainQty = this.#config?.matchMainQuantity !== false;

      bonuses.forEach((bonus) => {
        if (!expectedIds.includes(String(bonus.variant_id))) {
          updates.set(bonus.key, 0);
        }
      });

      const activeBonuses = bonuses.filter(
        (bonus) => expectedIds.includes(String(bonus.variant_id)) && !updates.has(bonus.key)
      );

      expected.forEach((bonusDef) => {
        const variantIdStr = String(bonusDef.variantId);
        const baseQty = parseInt(bonusDef.quantity, 10) || 1;
        const neededQty = matchMainQty ? baseQty * mainQty : baseQty;
        const existing = activeBonuses.find((b) => String(b.variant_id) === variantIdStr);

        if (!existing) {
          itemsToAdd.push({
            id: parseInt(bonusDef.variantId, 10),
            quantity: neededQty,
            parent_line_key: main.key,
            properties: {
              [BUNDLE_ID_PROP]: bundleId,
              [BUNDLE_ROLE_PROP]: 'bonus',
            },
          });
        }
      });
    });

    await this.#applyCartChanges(updates, itemsToAdd);
  }

  async #reconcile() {
    if (this.#processing) {
      this.#pendingReconcile = true;
      return;
    }
    this.#processing = true;

    try {
      let items = await this.#fetchCartItems();
      if (!items) return;

      if (!this.#config?.enabled) {
        await this.#removeOrphanBonuses(items);
        return;
      }

      const mainsToStamp = this.#findMainsToStamp(items);
      for (const entry of mainsToStamp) {
        await this.#stampMainLine(entry.item, entry.bundleId);
      }

      if (mainsToStamp.length > 0) {
        items = await this.#fetchCartItems();
        if (!items) return;
      }

      await this.#repairBonusLines(items);
    } catch (error) {
      console.error('[SubscriptionBundleManager]', error);
    } finally {
      this.#processing = false;
      if (this.#pendingReconcile) {
        this.#pendingReconcile = false;
        this.#scheduleReconcile(0);
      }
    }
  }

  /** @param {object[]} items */
  async #removeOrphanBonuses(items) {
    /** @type {Map<string, object>} */
    const itemsByKey = new Map(items.map((item) => [item.key, item]));
    /** @type {Map<string, number>} */
    const updates = new Map();

    items.forEach((item) => {
      if (!this.#isBonusLine(item, itemsByKey)) return;
      if (item.parent_relationship?.parent_key) return;

      const bundleId = item.properties?.[BUNDLE_ID_PROP];
      if (!bundleId) {
        updates.set(item.key, 0);
        return;
      }

      const hasMain = items.some(
        (candidate) =>
          candidate.properties?.[BUNDLE_ID_PROP] === bundleId &&
          candidate.properties?.[BUNDLE_ROLE_PROP] === 'main'
      );

      if (!hasMain) {
        updates.set(item.key, 0);
      }
    });

    await this.#applyCartChanges(updates, []);
  }
}

if (!window.__subscriptionBundleManager) {
  window.__subscriptionBundleManager = new SubscriptionBundleManager();
}
